import { useCallback, useReducer } from 'react';

const reducer = (previousState: PostPrivacyState, action: Action): PostPrivacyState => {
	switch (action.type) {
		case 'SET_SELECTED_USERS_IDS':
			return {
				selectedUsersIds: action.payload,
				internalOnly:
					'internalOnly' in previousState ? previousState.internalOnly : initialState.internalOnly,
			};
		case 'SET_ALL_STAFF_SELECTED':
			return {
				allStaffSelected: true,
			};
		case 'SET_SELECTED_TEAMS':
			return {
				selectedTeamIds: action.payload,
			};
		case 'SET_INTERNAL_ONLY':
			return {
				selectedUsersIds:
					'selectedUsersIds' in previousState
						? previousState.selectedUsersIds
						: initialState.selectedUsersIds,
				internalOnly: action.payload,
			};
		default:
			return previousState;
	}
};

type PostPrivacyState =
	| {
			selectedUsersIds: string[];
			internalOnly: boolean;
	  }
	| { allStaffSelected: boolean }
	| { selectedTeamIds: string[] };

type Action =
	| {
			type: 'SET_SELECTED_USERS_IDS';
			payload: string[];
	  }
	| { type: 'SET_ALL_STAFF_SELECTED' }
	| { type: 'SET_SELECTED_TEAMS'; payload: string[] }
	| { type: 'SET_INTERNAL_ONLY'; payload: boolean };

const initialState = {
	selectedUsersIds: [],
	internalOnly: false,
};

export const usePrivacyState = (defaultSelectedUsersIds?: string[], internalOnly?: boolean) => {
	const [state, dispatch] = useReducer(reducer, {
		...initialState,
		selectedUsersIds: defaultSelectedUsersIds ?? [],
		internalOnly: internalOnly ?? initialState.internalOnly,
	});

	const setSelectedUsersIds = useCallback(
		(selectedUsersIds: string[]) => {
			dispatch({ type: 'SET_SELECTED_USERS_IDS', payload: selectedUsersIds });
		},
		[dispatch],
	);

	const setAllStaffSelected = useCallback(() => {
		dispatch({ type: 'SET_ALL_STAFF_SELECTED' });
	}, [dispatch]);

	const setTeamSelected = useCallback(
		(teamIds: string[]) => {
			dispatch({ type: 'SET_SELECTED_TEAMS', payload: teamIds });
		},
		[dispatch],
	);

	const setInternalOnly = useCallback(
		(internalOnly: boolean) => {
			dispatch({ type: 'SET_INTERNAL_ONLY', payload: internalOnly });
		},
		[dispatch],
	);

	return {
		state,
		selectedUsersIds: 'selectedUsersIds' in state ? state.selectedUsersIds : [],
		selectedAllStaff: 'allStaffSelected' in state ? state.allStaffSelected : false,
		selectedTeamIds: 'selectedTeamIds' in state ? state.selectedTeamIds : undefined,
		isInternal: 'internalOnly' in state ? state.internalOnly : false,
		setSelectedUsersIds,
		setAllStaffSelected,
		setSelectedTeamsIds: setTeamSelected,
		setInternalOnly,
	};
};
